import type { HTMLAttributes, Ref } from 'react'
import { cx, formatNumber } from '@/shared/lib'
import s from './CounterBadge.module.css'

/**
 * Роль числа на своей поверхности.
 * `danger` — требует внимания (корзина, непрочитанные, просрочка);
 * `accent` — заполненный акцент на светлой подложке (мультиселект);
 * `accentTint` — счётчик раздела: пункт меню, активный таб;
 * `neutral` — приглушённый счётчик: неактивный таб;
 * `onAccent` — внутри акцентной заливки: число идёт цветом родителя прямо по заливке,
 * плашки под ним нет (она роняла контраст до 2.96:1 — разбор в модуле и контракте).
 */
export type CounterBadgeTone = 'danger' | 'accent' | 'accentTint' | 'neutral' | 'onAccent'

/** Ступени шкалы: xs — поверх иконки, sm — в потоке рядом с подписью, md — внутри контрола. */
export type CounterBadgeSize = 'xs' | 'sm' | 'md'

export type CounterBadgeProps = {
  /** Количество. Приходит с бэкенда готовым, компонент его не считает. */
  value: number
  /** Порог отображения: выше него рисуется «99+». Правило кита — значение по умолчанию, не константа разметки. */
  max?: number
  /** Показывать капсулу при нуле. По умолчанию ноль не рисуется вовсе. */
  showZero?: boolean
  tone?: CounterBadgeTone
  size?: CounterBadgeSize
  /**
   * Полная фраза для ассистивных технологий: «5 позиций в корзине».
   * Задана — цифры скрываются от скринридера, вместо них произносится фраза.
   * Не задана — смысл дают соседи по контролу («Заказы 12») либо родитель, объявивший всё сам.
   */
  label?: string
  /** Родитель уже назвал количество: капсула целиком уходит из дерева доступности. */
  decorative?: boolean
  ref?: Ref<HTMLSpanElement>
} & Omit<HTMLAttributes<HTMLSpanElement>, 'children'>

// Карта классов при noUncheckedIndexedAccess приходит как string | undefined
const TONE_CLASS: Record<CounterBadgeTone, string | undefined> = {
  danger: s.toneDanger,
  accent: s.toneAccent,
  accentTint: s.toneAccentTint,
  neutral: s.toneNeutral,
  onAccent: s.toneOnAccent,
}

const SIZE_CLASS: Record<CounterBadgeSize, string | undefined> = {
  xs: s.xs,
  sm: s.sm,
  md: s.md,
}

/** «NaN» и «−3» в шапке — заметный баг, поэтому мусор приводится к нулю, а дробное усекается. */
function normalize(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0
}

/**
 * Капсула с количеством: корзина, непрочитанные, счётчики табов и фильтров.
 * Позиционирование поверх иконки — забота родителя, приходит через className.
 * Контракт: docs/ui-kit/contracts/counter-badge.md
 */
export function CounterBadge({
  value,
  max = 99,
  showZero = false,
  tone = 'accentTint',
  size = 'sm',
  label,
  decorative = false,
  className,
  ref,
  ...rest
}: CounterBadgeProps) {
  const count = normalize(value)

  if (count === 0 && !showZero) return null

  // «+» — типографский маркер переполнения, одинаковый во всех локалях, поэтому не ключ i18n
  const text = count > max ? `${formatNumber(max)}+` : formatNumber(count)
  const announced = Boolean(label) && !decorative

  return (
    <span
      {...rest}
      ref={ref}
      className={cx(s.root, SIZE_CLASS[size], TONE_CLASS[tone], className)}
      data-tone={tone}
      data-size={size}
      aria-hidden={decorative || undefined}
    >
      <span aria-hidden={announced || undefined}>{text}</span>
      {announced ? <span className={s.label}>{label}</span> : null}
    </span>
  )
}
